import { clsx } from 'clsx';
import { type TFunction } from 'i18next';
import {
  ChevronRight,
  Circle,
  FileSearch,
  Layers,
  Loader,
  SearchCheck,
  TextSearch,
  type LucideIcon,
} from 'lucide-react';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type MessageStep } from '@/util/interfaces';

interface StepPresentation {
  /** Shown once the step is done. An active step always shows the spinner. */
  icon: LucideIcon;
  /** Present participle - what the agent is doing right now. */
  active: string;
  /** Past tense - what the agent did. Every step needs its own: falling back
   *  to the active label leaves a finished step reading as still running. */
  done: string;
}

// Backend step keys are stable identifiers, not user-facing text - this is the
// one place that maps them to copy and an icon, so a new step key degrades to
// showing the raw key rather than a blank row.
const STEP_PRESENTATION: Record<string, StepPresentation> = {
  reviewing_question: {
    icon: SearchCheck,
    active: 'prop-chat.step-reviewing-question',
    done: 'prop-chat.step-reviewing-question-done',
  },
  gathering_context: {
    icon: Layers,
    active: 'prop-chat.step-gathering-context',
    done: 'prop-chat.step-gathering-context-done',
  },
};

interface StepRowProps {
  icon: LucideIcon;
  label: string;
  spinning?: boolean;
}

const StepRow: FC<StepRowProps> = ({ icon: Icon, label, spinning = false }) => (
  <li className="flex w-full items-start gap-[var(--ds-space-2)]">
    {/* One text line tall, so the glyph stays centred on the first line of a
        label that wraps onto a second. */}
    <span className="flex h-[var(--ds-typography-paragraph-13-regular-line-height)] shrink-0 items-center">
      <Icon
        aria-hidden="true"
        className={clsx('size-[var(--ds-space-4)]', spinning && 'animate-spin')}
      />
    </span>
    <span className="min-w-0 flex-1">{label}</span>
  </li>
);

/**
 * The completion detail is backend-rendered English summarising what was read,
 * e.g. "Reviewed 24 findings and 3 documents". The design gives it a row of its
 * own under the step that produced it, with an icon for what was read - so the
 * icon is picked from the sentence until the backend sends the counts as data.
 */
const detailIcon = (detail: string): LucideIcon =>
  /finding/i.test(detail) ? TextSearch : FileSearch;

const stepRows = (step: MessageStep, t: TFunction) => {
  const presentation = STEP_PRESENTATION[step.key];
  const isActive = step.status === 'active';
  const rows = [
    <StepRow
      key={step.key}
      icon={isActive ? Loader : (presentation?.icon ?? Circle)}
      spinning={isActive}
      label={presentation ? t(isActive ? presentation.active : presentation.done) : step.key}
    />,
  ];

  // Not translated: the detail is a sentence the backend renders in English.
  if (step.detail) {
    rows.push(
      <StepRow key={`${step.key}-detail`} icon={detailIcon(step.detail)} label={step.detail} />,
    );
  }

  return rows;
};

interface StepIndicatorProps {
  steps: MessageStep[];
  /**
   * How long the agent thought for. Undefined while it is still thinking - and
   * on a message stored before the timing was recorded - when the steps read as
   * a plain list with no summary to collapse them under.
   */
  thinkingSeconds?: number;
}

export const StepIndicator: FC<StepIndicatorProps> = ({ steps, thinkingSeconds }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (steps.length === 0) return null;

  const stepList = (
    // The 32px indent lines the icons up with the agent name beside the logo.
    <ul className="m-0 flex w-full list-none flex-col gap-[var(--ds-space-2)] p-0 pl-[var(--ds-space-8)] font-[var(--ds-typography-font-definitions-font-family-body)] text-[length:var(--ds-typography-paragraph-13-regular-font-size)] font-[var(--ds-typography-paragraph-13-regular-font-weight)] leading-[var(--ds-typography-paragraph-13-regular-line-height)] tracking-[var(--ds-typography-paragraph-mini-letter-spacing)] text-[var(--ds-color-raw-stone-600)]">
      {steps.flatMap((step) => stepRows(step, t))}
    </ul>
  );

  if (thinkingSeconds === undefined) return stepList;

  return (
    <div className="flex w-full flex-col">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        className="flex w-full cursor-pointer items-center gap-[var(--ds-space-2)] rounded-[var(--ds-radius-8)] px-[var(--ds-space-2)] font-[var(--ds-typography-font-definitions-font-family-body)] text-[length:var(--ds-typography-paragraph-13-regular-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-paragraph-mini-line-height)] tracking-[var(--ds-typography-paragraph-mini-letter-spacing)] text-[var(--ds-color-raw-stone-600)]"
      >
        {/* A quarter turn takes the collapsed chevron to the one the expanded
            frame shows, so the arrow turns rather than being swapped out. */}
        <ChevronRight
          aria-hidden="true"
          className={clsx(
            'size-[var(--ds-space-4)] shrink-0 transition-transform duration-200 ease-out motion-reduce:transition-none',
            expanded && 'rotate-90',
          )}
        />
        <span className="min-w-0 flex-1 text-left">
          {t('prop-chat.steps-thought-for', { seconds: thinkingSeconds })}
        </span>
      </button>
      {/* 0fr -> 1fr animates to the list's own height, which a max-height guess
          could only approximate. The gap sits inside the collapsing box so it
          closes up with the list rather than leaving a stray 8px behind. */}
      <div
        aria-hidden={!expanded}
        className={clsx(
          'grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none',
          expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <div className="pt-[var(--ds-space-2)]">{stepList}</div>
        </div>
      </div>
    </div>
  );
};
